import { Link } from "react-router-dom";
import HeroSlider from "@/pages/home/components/HeroSlider";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import RankList from "@/pages/home/components/RankList";
import TopicGrid from "@/pages/home/components/TopicGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

const stats = [
  { value: "24", label: "部影片资料" },
  { value: "04", label: "大分类" },
  { value: "02", label: "覆盖地区" },
  { value: "05", label: "年新片跨度" },
];

export default function Home() {
  useSeo({
    title: "日韩伦理电影 - 最新日韩色情电影资源与剧情介绍",
    description:
      "日韩伦理电影网站提供最新的日韩色情电影资源，涵盖丰富的电影剧情介绍和分类导航，方便用户查找和浏览各类热门伦理电影作品。",
    keywords: "日韩伦理电影,日韩电影,伦理电影,日韩影视,剧情介绍,电影资源,情色电影,影视导航",
  });

  const latest = [...movies]
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, 12);

  const editorsPick = [...movies]
    .filter((movie) => movie.genres.includes("经典"))
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 4);

  return (
    <>
      <HeroSlider />

      <section className="border-b border-background-200/60 bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-1.5 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-primary-300">
                <span className="h-px w-6 bg-primary-500/70"></span>
                Browse by genre
              </p>
              <h2 className="text-lg font-semibold text-foreground-950 md:text-xl">按类型快速进入</h2>
            </div>
            <p className="text-[12.5px] text-foreground-500">四个方向，覆盖日韩伦理题材的主要脉络</p>
          </div>
          <CategoryQuickLinks />
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            eyebrow="New arrivals"
            title="最新上架"
            description="按年份与热度排序的最新影片资料，点击封面即可查看完整剧情、导演与主演信息。"
            actionLabel="进入分类页筛选"
            actionTo="/category/伦理"
          />
          <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {latest.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-background-200/60 bg-background-100/40">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            eyebrow="Trending & features"
            title="热门推荐与专题策划"
            description="从热度榜单到主题片单，换一种方式发现值得一看的作品。"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <RankList />
            </div>
            <div className="lg:col-span-7">
              <TopicGrid />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            eyebrow="Editor's choice"
            title="编辑精选 · 值得回看"
            description="被时间留下来的作品，往往在剧情之外还留下了一点别的什么。"
            actionLabel="更多经典作品"
            actionTo="/category/经典"
          />
          <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
            {editorsPick.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-background-200/60 bg-accent-950">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <p className="mb-3 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-accent-200">
              <span className="h-px w-6 bg-accent-400/70"></span>
              About yelan
            </p>
            <h2 className="font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-[30px]">
              一个不提供播放的
              <br className="hidden sm:block" />
              日韩伦理电影资料库
            </h2>
            <p className="mt-5 max-w-xl text-[13.5px] leading-[1.9] text-foreground-500">
              夜澜专注于整理日韩伦理题材作品的基础资料：年份、地区、导演、主演、类型与剧情梗概。
              我们不做资源聚合，也不提供任何播放或下载入口——只把影片本身讲清楚，剩下的判断留给你。
            </p>
            <p className="mt-3 max-w-xl text-[13.5px] leading-[1.9] text-foreground-500">
              站内所有影片信息均为演示用虚构数据，用于展示分类、检索与收藏的产品形态。
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-[12.5px] font-medium text-background-50 transition-colors hover:bg-primary-500"
              >
                了解站点定位
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/search?q="
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-4 py-2.5 text-[12.5px] text-foreground-700 transition-colors hover:border-primary-500 hover:text-primary-200"
              >
                <i className="ri-search-line"></i>
                直接搜索片名
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-accent-800/50 bg-accent-900/40 p-4 md:p-5"
                >
                  <p className="font-label text-3xl font-semibold leading-none text-secondary-300">{stat.value}</p>
                  <p className="mt-2 text-[12px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 rounded-lg border border-primary-900/60 bg-primary-950/30 px-4 py-3 text-[11.5px] leading-relaxed text-primary-200/80">
              <i className="ri-information-line mr-1"></i>
              内容仅限 18 岁以上成年观众浏览。本站不存储、不传播任何影片文件。
            </p>
          </div>
        </div>
      </section>
    </>
  );
}